import React, { useState, useEffect, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import ModalAllder from "components/layout/ModalAllder";
import AllderTable from "components/layout/AllderTable";
import { NumericFormat } from "react-number-format";
import { useDispatch } from "react-redux";
import { nanoid } from "@reduxjs/toolkit";
import { memberArInvoiceHandler } from "../../../../redux/handlers";

function fmtDate(dateVal) {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// คำนวณ VAT แบบ inclusive (ราคารวม VAT แล้ว)
// taxable = price / 1.07,  vat = price - taxable
const calcVat = (price, isTaxInvoice) => {
  const p = Number(price) || 0;
  if (!isTaxInvoice || p === 0) {
    return { taxable_amount: p, vat: 0, total_amount: p };
  }
  const taxable = Math.round((p / 1.07) * 100) / 100;
  const vat = Math.round((p - taxable) * 100) / 100;
  return { taxable_amount: taxable, vat, total_amount: p };
};

export default function ModalLoadDocument({
  modal,
  setModal,
  memberId,
  businessSlug,
  productRow = [],
  setProductRow,
  isTaxInvoice = false,
}) {
  const dispatch = useDispatch();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // Ref เพื่อให้ useEffect อ่าน productRow ล่าสุดเสมอ (ป้องกัน stale closure)
  const productRowRef = useRef(productRow);
  productRowRef.current = productRow;

  // ─── Fetch debt transactions when modal opens ────────────────
  const fetchDocs = useCallback(async () => {
    if (!modal.open || !memberId || !businessSlug) return;
    try {
      setLoading(true);
      const result = await dispatch(
        memberArInvoiceHandler.getMemberDebtTransactions(
          businessSlug,
          memberId,
          filterText || undefined,
        ),
      );
      setDocs(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Error fetching debt transactions:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, modal.open, memberId, businessSlug, filterText]);

  useEffect(() => {
    if (modal.open) {
      const loadedIds = productRowRef.current
        .map((r) => r.member_debt_transaction_id)
        .filter(Boolean);
      setSelectedIds(loadedIds);
      fetchDocs();
    }
  }, [modal.open]);

  const handleSearch = () => {
    fetchDocs();
  };

  // ─── Columns ────────────────────────────────────────────────
  const columns = [
    {
      field: "documentNumber",
      headerName: "เลขที่เอกสาร",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <span style={{ color: "#565DFF", fontWeight: 500 }}>
          {row.documentNumber}
        </span>
      ),
    },
    {
      field: "documentDate",
      headerName: "วันที่เอกสาร",
      width: 140,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <span className="text-[#FF0000] font-medium">
          {fmtDate(row.documentDate)}
        </span>
      ),
    },
    {
      field: "quantity",
      headerName: "จำนวนสินค้า",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "excludeVat",
      headerName: "มูลค่าก่อนภาษี",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const { taxable_amount } = calcVat(row.price, isTaxInvoice);
        return (
          <span className="text-[#FF0000]">
            <NumericFormat
              displayType="text"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
              value={taxable_amount}
            />
          </span>
        );
      },
    },
    {
      field: "vat",
      headerName: "มูลค่าภาษี",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const { vat } = calcVat(row.price, isTaxInvoice);
        return (
          <NumericFormat
            displayType="text"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            value={vat}
          />
        );
      },
    },
    {
      field: "netValue",
      headerName: "มูลค่าสุทธิ",
      width: 130,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const { total_amount } = calcVat(row.price, isTaxInvoice);
        return (
          <NumericFormat
            displayType="text"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            value={total_amount}
          />
        );
      },
    },
    {
      field: "alreadyInvoiced",
      headerName: "สถานะ",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) =>
        row.alreadyInvoiced ? (
          <Chip
            label="ออกใบแจ้งหนี้แล้ว"
            size="small"
            className="!text-[11px] !text-[#FDB428] !bg-[#FFF6E5]"
          />
        ) : (
          <Chip
            label="ยังไม่ออก"
            size="small"
            className="!text-[11px] !text-[#2DC76D] !bg-[#EEFBE5]"
          />
        ),
    },
  ];

  // ─── Confirm: convert selected to product rows ───────────────
  const handleConfirm = () => {
    const selected = docs.filter((d) => selectedIds.includes(d.id));
    if (!selected.length) return;

    const loadedIds = productRow
      .map((r) => r.member_debt_transaction_id)
      .filter(Boolean);

    const newRows = selected
      .filter((d) => !loadedIds.includes(d.id))
      .map((d) => {
        const { taxable_amount, vat, total_amount } = calcVat(
          d.price,
          isTaxInvoice,
        );
        return {
          rowID: nanoid(),
          member_debt_transaction_id: d.id,
          documentNumber: d.documentNumber,
          documentDate: d.documentDate,
          quantity: d.quantity ?? 1,
          price_per_unit: d.price,
          discount_type: "amount",
          discount_rate: null,
          taxable_amount,
          vat,
          total_amount,
          remark: "",
          is_checked: false,
          edit: false,
        };
      });

    setProductRow((prev) => {
      // กรอง blank rows ออกก่อน แล้วต่อด้วย rows ที่เลือกมา
      const nonBlank = prev.filter(
        (r) =>
          r.documentNumber !== "" ||
          (r.price_per_unit !== "" && r.price_per_unit != null),
      );
      return [...nonBlank, ...newRows];
    });
    setModal((p) => ({ ...p, open: false }));
    setSelectedIds([]);
  };

  return (
    <ModalAllder modal={modal} setModal={setModal} htitle="โหลดเอกสาร" hbtn>
      <div className="flex items-center mb-4 mt-3 gap-3">
        <TextField
          label=""
          variant="outlined"
          size="small"
          placeholder="เลขที่เอกสาร"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          onKeyUp={(e) => e.key === "Enter" && handleSearch()}
          sx={{ width: 260 }}
        />
        <Button
          variant="contained"
          color="primary"
          size="large"
          className="!px-5"
          onClick={handleSearch}
        >
          ค้นหา
        </Button>
        <div className="text-[#2DC76D] bg-[#F4F4F4] drop-shadow rounded ml-auto px-3 mr-3 py-2">
          เลือกไว้ {selectedIds.length} รายการ
        </div>
      </div>

      <AllderTable
        getRowId={(row) => row.id}
        rows={docs}
        columns={columns}
        loading={loading}
        checkboxSelection
        isRowSelectable={(params) => !params.row.alreadyInvoiced}
        onRowModelChange={(ids) => setSelectedIds(ids)}
        selectionModel={selectedIds}
      />

      <div className="text-right mt-5">
        <Button
          variant="outlined"
          color="gray"
          className="!px-10"
          onClick={() => setModal((p) => ({ ...p, open: false }))}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="!px-10 !ml-4"
          disabled={selectedIds.length === 0}
          onClick={handleConfirm}
        >
          ยืนยัน ({selectedIds.length})
        </Button>
      </div>
    </ModalAllder>
  );
}
